"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  BODIES,
  type BodyData,
  findBody,
  jumpHeight,
  massKg,
  type WeightUnit,
  weightOn,
} from "@/lib/tool-kit/astronomy/planets";

/** English strings. A translated content file overrides these through `ui`. */
export const PLANET_WEIGHT_CALCULATOR_STRINGS = {
  amount: "Your weight on Earth",
  unit: "Unit",
  unitNames: {
    kg: "Kilograms (scale reading)",
    lb: "Pounds",
    N: "Newtons",
  },
  unitSymbols: { kg: "kg", lb: "lb", N: "N" },
  jump: "How high you can jump on Earth (optional)",
  jumpUnit: "Jump unit",
  jumpUnits: { cm: "centimetres", in: "inches" },
  jumpSymbols: { cm: "cm", in: "in" },
  compare: "Show the result for",
  bodyNames: {
    sun: "the Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth",
    moon: "the Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  bodyHeads: {
    sun: "Sun",
    mercury: "Mercury",
    venus: "Venus",
    earth: "Earth",
    moon: "Moon",
    mars: "Mars",
    jupiter: "Jupiter",
    saturn: "Saturn",
    uranus: "Uranus",
    neptune: "Neptune",
    pluto: "Pluto",
  },
  invalid: "Enter a weight greater than zero.",
  invalidJump: "Enter a jump height greater than zero, or leave it blank.",
  result: "On {body} you would weigh",
  weightLabel: "Your weight there",
  weightValue: "{value} {unit}",
  ratio: "Compared with Earth",
  ratioValue: "{value} ×",
  gravity: "Surface gravity",
  gravityValue: "{value} m/s²",
  force: "Force on you",
  forceValue: "{value} N",
  jumpResult: "Your jump there",
  jumpValue: "{value} {unit}",
  massLine: "Your mass is {mass} kg on every world; only the weight changes.",
  tableHeading: "Your weight on every world",
  bodyColumn: "World",
  gravityColumn: "Gravity (m/s²)",
  ratioColumn: "× Earth",
  weightColumn: "Your weight",
  jumpColumn: "Your jump",
  barColumn: "Compared with Earth",
  barLabel: "{body}: {ratio} times Earth's weight",
  noSurface:
    "The Sun, Jupiter, Saturn, Uranus and Neptune have no solid surface. Their weights are for the visible surface or the 1-bar pressure level, where you could never stand; they show what the gravity there would do.",
  copy: "Copy result",
  copied: "Copied!",
  summary:
    "{amount} {unit} on Earth is {weight} {unit} on {body} (surface gravity {gravity} m/s², {ratio} × Earth).",
  source:
    "Gravity values are NASA's mean surface gravity from its Planetary Fact Sheets; Earth is the standard 9.80665 m/s².",
} as const;

type Strings = typeof PLANET_WEIGHT_CALCULATOR_STRINGS;

const UNITS: readonly WeightUnit[] = ["kg", "lb", "N"];
/** The largest ratio among the planets, the Moon and Pluto: Jupiter's. */
const BAR_SCALE = 2.7;

function decimalsFor(value: number): number {
  if (value >= 1000) {
    return 0;
  }
  return value >= 100 ? 1 : 2;
}

function PickedResult({
  body,
  value,
  unit,
  jumpValue,
  jumpUnit,
  strings,
}: {
  body: BodyData;
  value: number;
  unit: WeightUnit;
  jumpValue: number | null;
  jumpUnit: "cm" | "in";
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const names = strings.bodyNames as Record<string, string>;
  const symbol = strings.unitSymbols[unit];
  const show = (n: number, places = decimalsFor(n)) =>
    fmt(n, { minimumFractionDigits: places, maximumFractionDigits: places });
  const w = weightOn(body, value, unit);
  const name = names[body.id] ?? body.id;
  return (
    <>
      <h2 className={WIDGET_H2}>{t(strings.result, { body: name })}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={t(strings.result, { body: name })}
          value={t(strings.weightValue, {
            value: show(w.weight),
            unit: symbol,
          })}
        />
        <StatCard
          label={strings.ratio}
          value={t(strings.ratioValue, {
            value: fmt(w.ratio, {
              minimumFractionDigits: 3,
              maximumFractionDigits: 3,
            }),
          })}
        />
        <StatCard
          label={strings.gravity}
          value={t(strings.gravityValue, {
            value: fmt(w.gravity, { maximumFractionDigits: 5 }),
          })}
        />
        <StatCard
          label={strings.force}
          value={t(strings.forceValue, { value: show(w.newtons) })}
        />
        {jumpValue !== null && jumpValue > 0 ? (
          <StatCard
            label={strings.jumpResult}
            value={t(strings.jumpValue, {
              value: show(jumpHeight(jumpValue, body), 1),
              unit: strings.jumpSymbols[jumpUnit],
            })}
          />
        ) : null}
      </StatGrid>
      <p className="text-foreground text-sm">
        {t(strings.massLine, { mass: show(massKg(value, unit)) })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.summary, {
          amount: show(value),
          unit: symbol,
          weight: show(w.weight),
          body: name,
          gravity: fmt(w.gravity, { maximumFractionDigits: 5 }),
          ratio: fmt(w.ratio, { maximumFractionDigits: 3 }),
        })}
      />
    </>
  );
}

export default function PlanetWeightCalculatorWidget() {
  const strings: Strings = useToolUi(PLANET_WEIGHT_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [amount, setAmount] = useState("150");
  const [unit, setUnit] = useState<WeightUnit>("lb");
  const [jump, setJump] = useState("");
  const [jumpUnit, setJumpUnit] = useState<"cm" | "in">("cm");
  const [pick, setPick] = useState("mars");

  const value = parseNumberInput(amount);
  const jumpValue = jump.trim() === "" ? null : parseNumberInput(jump);
  const jumpBad = jump.trim() !== "" && (jumpValue === null || jumpValue <= 0);
  const valid = value !== null && value > 0;
  const heads = strings.bodyHeads as Record<string, string>;
  const symbol = strings.unitSymbols[unit];
  const jumpSymbol = strings.jumpSymbols[jumpUnit];
  const show = (n: number, places = decimalsFor(n)) =>
    fmt(n, { minimumFractionDigits: places, maximumFractionDigits: places });
  const body = findBody(pick) ?? BODIES[0];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="pw-amount"
          label={strings.amount}
          numeric
          onChange={setAmount}
          value={amount}
        />
        <PillGroup
          label={strings.unit}
          name="pw-unit"
          onChange={setUnit}
          options={UNITS.map((u) => ({
            value: u,
            label: strings.unitNames[u],
          }))}
          value={unit}
        />
        <TextField
          id="pw-jump"
          label={strings.jump}
          numeric
          onChange={setJump}
          value={jump}
        />
        <SelectField
          id="pw-jump-unit"
          label={strings.jumpUnit}
          onChange={setJumpUnit}
          options={(["cm", "in"] as const).map((u) => ({
            value: u,
            label: strings.jumpUnits[u],
          }))}
          value={jumpUnit}
        />
      </div>
      <div aria-live="polite" className="space-y-6">
        {valid ? null : <ErrorNotice>{strings.invalid}</ErrorNotice>}
        {jumpBad ? <ErrorNotice>{strings.invalidJump}</ErrorNotice> : null}
        {valid && value !== null ? (
          <>
            <section className="space-y-4">
              <SelectField
                id="pw-body"
                label={strings.compare}
                onChange={setPick}
                options={BODIES.filter((b) => b.id !== "earth").map((b) => ({
                  value: b.id,
                  label: heads[b.id] ?? b.id,
                }))}
                value={pick}
              />
              <PickedResult
                body={body}
                jumpUnit={jumpUnit}
                jumpValue={jumpValue}
                strings={strings}
                unit={unit}
                value={value}
              />
            </section>
            <section className={`${CARD} space-y-3`}>
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.bodyColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.gravityColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.ratioColumn}
                      </th>
                      <th className={TH} scope="col">
                        {strings.weightColumn}
                      </th>
                      {jumpValue !== null && jumpValue > 0 ? (
                        <th className={TH} scope="col">
                          {strings.jumpColumn}
                        </th>
                      ) : null}
                      <th className={TH} scope="col">
                        {strings.barColumn}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {BODIES.map((b) => {
                      const w = weightOn(b, value, unit);
                      return (
                        <tr
                          className={b.id === pick ? "bg-primary/10" : ""}
                          key={b.id}
                        >
                          <td className={`${TD} font-medium`}>
                            {heads[b.id] ?? b.id}
                          </td>
                          <td className={TD}>
                            {fmt(w.gravity, { maximumFractionDigits: 5 })}
                          </td>
                          <td className={TD}>
                            {fmt(w.ratio, {
                              minimumFractionDigits: 3,
                              maximumFractionDigits: 3,
                            })}
                          </td>
                          <td className={TD}>
                            {t(strings.weightValue, {
                              value: show(w.weight),
                              unit: symbol,
                            })}
                          </td>
                          {jumpValue !== null && jumpValue > 0 ? (
                            <td className={TD}>
                              {t(strings.jumpValue, {
                                value: show(jumpHeight(jumpValue, b), 1),
                                unit: jumpSymbol,
                              })}
                            </td>
                          ) : null}
                          <td className={`${TD} min-w-28`}>
                            {b.id === "sun" ? (
                              "—"
                            ) : (
                              <div
                                aria-label={t(strings.barLabel, {
                                  body: heads[b.id] ?? b.id,
                                  ratio: fmt(w.ratio, {
                                    maximumFractionDigits: 2,
                                  }),
                                })}
                                className="h-2 rounded bg-primary"
                                role="img"
                                style={{
                                  width: `${Math.round((w.ratio / BAR_SCALE) * 100)}%`,
                                }}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        ) : null}
      </div>
      <Notice>{strings.noSurface}</Notice>
      <p className="text-muted-foreground text-xs">{strings.source}</p>
    </div>
  );
}
